import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/subscriptions/plans").then(({ data }) => setPlans(data)); }, []);

  const subscribe = async (planId) => {
    if (!user) { navigate("/login", { state: { from: "/pricing" } }); return; }
    setBusy(planId);
    try {
      const { data } = await api.post("/subscriptions/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(null);
    }
  };

  const activePlan = user?.subscription?.status === "active" ? user.subscription.plan_id : null;

  return (
    <div className="bg-[#090A0F] min-h-screen"><Header />
      <div className="max-w-5xl mx-auto px-6 pt-32 pb-16 fade-up">
        <div className="text-center mb-14">
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Membership</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white mb-3">Buy once. Produce all month.</h1>
          <p className="text-slate-400 max-w-xl mx-auto">Subscribe for monthly listing-film credits. Each credit produces a full listing cinematic — no per-order checkout.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {plans.map((p) => (
            <div key={p.id}
              className={`relative rounded-2xl p-8 flex flex-col bg-gradient-to-b from-[#181B26] to-[#12141D] border transition-all
                ${p.id === "pro" ? "border-[#D4AF37]/50 gold-glow" : "border-[#D4AF37]/15"}`}
              data-testid={`plan-card-${p.id}`}>
              {p.id === "pro" && <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-[#D4AF37] text-black text-xs font-semibold flex items-center gap-1"><Crown className="w-3 h-3" /> Best Value</span>}
              <h3 className="font-display text-3xl text-white">{p.name}</h3>
              <p className="text-slate-400 text-sm mt-1 mb-5">{p.blurb}</p>
              <p className="text-4xl font-mono text-white">${p.price}<span className="text-base text-slate-500">/mo</span></p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="text-sm text-slate-300 flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" /> {f}</li>
                ))}
              </ul>
              <Button onClick={() => subscribe(p.id)} disabled={busy === p.id || activePlan === p.id}
                className={`mt-7 rounded-full h-12 ${p.id === "pro" ? "bg-[#D4AF37] text-black hover:bg-[#E2C044]" : "bg-white/5 text-white border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10"}`}
                data-testid={`subscribe-${p.id}`}>
                {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : activePlan === p.id ? "Current Plan" : `Subscribe — $${p.price}/mo`}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-500 mt-8">Cancel anytime. Credits apply to listing films (30s & 60s cinematics).</p>
      </div>
      <Footer />
    </div>
  );
}
